import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, Shield, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';
import axios from 'axios';
import { trackFormSubmission, trackWhatsAppClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'AI Growth System',
    'Social Media & GBP Management',
    'Google Ads & Meta Ads',
    'Website Design & Management',
    'SEO Services',
    'Content Marketing',
    'Not Sure Yet'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: `Service Interest: ${formData.service}\n\n${formData.message}`
      });
      
      if (response.status === 200) {
        trackFormSubmission('contact', {
          name: formData.name,
          has_phone: !!formData.phone,
          has_email: !!formData.email,
        });
        
        toast.success("Message Sent Successfully!", {
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      }
    } catch (error) {
      // Error submitting contact form - show user-friendly message
      toast.error("Failed to Send Message", {
        description: "Please try again or contact us via WhatsApp.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    trackWhatsAppClick('contact_page');
    window.open(`https://wa.me/91${contactInfo.whatsappNumber}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Premium Header */}
        <div className="text-center mb-20 animate-fade-in-up">
          <div className="inline-block mb-6">
            <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">Contact Us</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight">
            Let's Build Your <span className="text-indigo-400">Growth System</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-light">
            Share your goals with us. We'll create a customized strategy to transform your digital presence and drive measurable results.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Premium Contact Form */}
          <div className="lg:col-span-2 animate-fade-in-up stagger-2">
            <Card className="glass-premium p-10 md:p-12 rounded-3xl border border-white/10">
              <h2 className="text-3xl font-bold text-white mb-3">Get Your Free Consultation</h2>
              <p className="text-gray-400 mb-8 font-light">Fill out the form and we'll reach out within 24 hours</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-3">
                  <Label htmlFor="name" className="text-gray-300 text-sm font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="premium-input bg-white/5 border-white/10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-white placeholder-gray-500 rounded-xl h-14 px-6 text-lg transition-all duration-300"
                    placeholder="John Doe"
                  />
                </div>

                {/* Phone & Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="phone" className="text-gray-300 text-sm font-medium">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="premium-input bg-white/5 border-white/10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-white placeholder-gray-500 rounded-xl h-14 px-6 text-lg transition-all duration-300"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="email" className="text-gray-300 text-sm font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="premium-input bg-white/5 border-white/10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-white placeholder-gray-500 rounded-xl h-14 px-6 text-lg transition-all duration-300"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div className="space-y-3">
                  <Label htmlFor="service" className="text-gray-300 text-sm font-medium">
                    Service Interest *
                  </Label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-white rounded-xl h-14 px-6 text-lg transition-all duration-300 appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='rgba(255,255,255,0.4)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1.5rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="" className="bg-gray-900">Select a service...</option>
                    {services.map((service) => (
                      <option key={service} value={service} className="bg-gray-900 text-white">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div className="space-y-3">
                  <Label htmlFor="message" className="text-gray-300 text-sm font-medium">
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="premium-input bg-white/5 border-white/10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-white placeholder-gray-500 rounded-xl resize-none px-6 py-4 text-lg transition-all duration-300"
                    placeholder="Tell us about your business goals and how we can help..."
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white px-8 h-16 text-lg rounded-full font-semibold shadow-2xl shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-500 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <>
                      Send Message <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center justify-center gap-6 pt-4 text-sm text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Shield size={16} className="text-indigo-400" />
                    <span>100% Secure</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock size={16} className="text-indigo-400" />
                    <span>24h Response Time</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle size={16} className="text-indigo-400" />
                    <span>No Spam, Ever</span>
                  </div>
                </div>
              </form>
            </Card>
          </div>

          {/* Contact Cards */}
          <div className="space-y-6 animate-fade-in-up stagger-3">
            {/* WhatsApp Card - Dominant */}
            <Card 
              onClick={handleWhatsApp}
              className="glass-premium glow-subtle p-8 rounded-3xl cursor-pointer group hover:scale-105 transition-all duration-500 border-2 border-green-500/30 hover:border-green-500/60 bg-gradient-to-br from-green-900/20 to-transparent"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-500/50">
                  <MessageCircle className="text-white" size={28} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-xl">Instant Response</h3>
                  <p className="text-green-400 text-sm font-medium">Chat on WhatsApp</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Get immediate answers to your questions. Our team is ready to help you right now.
              </p>
              <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full h-12 font-medium shadow-lg shadow-green-500/30">
                Start Chat <MessageCircle className="ml-2" size={18} />
              </Button>
            </Card>

            {/* Email Card */}
            <Card className="glass-premium premium-card p-6 rounded-3xl group border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-indigo-500/10 rounded-2xl group-hover:bg-indigo-500/20 transition-colors duration-300">
                  <Mail className="text-indigo-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Email Us</h3>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-gray-400 hover:text-indigo-400 transition-colors duration-300 break-all text-sm"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              </div>
            </Card>

            {/* Phone Card */}
            <Card className="glass-premium premium-card p-6 rounded-3xl group border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-indigo-500/10 rounded-2xl group-hover:bg-indigo-500/20 transition-colors duration-300">
                  <Phone className="text-indigo-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Call Us</h3>
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="text-gray-400 hover:text-indigo-400 transition-colors duration-300 text-sm"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              </div>
            </Card>

            {/* Location Card */}
            <Card className="glass-premium premium-card p-6 rounded-3xl group border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-indigo-500/10 rounded-2xl group-hover:bg-indigo-500/20 transition-colors duration-300">
                  <MapPin className="text-indigo-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Global Reach</h3>
                  <p className="text-gray-400 text-sm">
                    Serving clients worldwide
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Business Hours */}
        <div className="mb-16 animate-fade-in-up stagger-4">
          <Card className="glass-premium p-10 rounded-3xl border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Business Hours</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-gray-400 font-medium">Monday - Friday</span>
                <span className="text-white font-semibold">9:00 AM - 7:00 PM</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-gray-400 font-medium">Saturday</span>
                <span className="text-white font-semibold">10:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-gray-400 font-medium">Sunday</span>
                <span className="text-white font-semibold">Closed</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-gray-400 font-medium">Emergency Support</span>
                <span className="text-green-400 font-semibold">24/7 WhatsApp</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Final CTA */}
        <div className="text-center animate-fade-in-up stagger-5">
          <Card className="glass-premium glow-subtle p-12 rounded-3xl border border-white/10">
            <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">
              Ready to <span className="text-indigo-400">Transform</span> Your Business?
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto font-light">
              Join hundreds of businesses leveraging AI-powered marketing for explosive growth
            </p>
            <Button
              onClick={handleWhatsApp}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-12 py-6 text-lg rounded-full font-semibold shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 transition-all duration-500 hover:scale-105"
            >
              <MessageCircle className="mr-2" size={24} /> Chat on WhatsApp Now
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;
